import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Download, ArrowRight, Mail, Shield } from 'lucide-react';
import { API_URL } from '../config';
import { trackPurchase } from '../utils/metaPixel';
import '../styles/ParentsHandbook.css';

const API_BASE = `${API_URL}/api`;
// Fallback only used if the backend pdf-url endpoint is briefly unreachable.
// Always kept in sync with handbook_router.HANDBOOK_PDF_URL on every PDF release.
const HANDBOOK_PDF_FALLBACK = 'https://customer-assets.emergentagent.com/job_353394c3-8209-4310-bcf8-c535c3e3d519/artifacts/43hkmiqm_parents_digital_safety_handbook_final13%20%288%29.pdf';

const ParentsHandbookThankYou = () => {
  const [searchParams] = useSearchParams();
  const [showConfetti, setShowConfetti] = useState(true);
  const [handbookUrl, setHandbookUrl] = useState(HANDBOOK_PDF_FALLBACK);

  const amount = searchParams.get('amount') || '9.99';

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  // Meta Pixel — fire Purchase ONLY after verifying the Stripe session was actually paid.
  // This prevents Meta from recording phantom conversions when users land on the
  // thank-you page without completing payment (e.g., abandoned Stripe Checkout,
  // browser back-navigation, or direct URL visits).
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return; // No session to verify → don't fire Purchase

    fetch(`${API_BASE}/handbook/session-status?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.paid) {
          console.warn('[Handbook] Stripe session not paid — skipping Meta Purchase event', data);
          return;
        }
        const valueDollars = (data.amount_cents || 0) / 100;
        // Deterministic event_id — must match server CAPI event_id sent from
        // the Stripe webhook (`purchase_{stripe_session_id}`) so Meta deduplicates.
        trackPurchase({
          value: valueDollars,
          order_id: sessionId,
          content_id: 'parents-handbook',
          content_name: "The Parents' Digital Safety Handbook",
          event_id: `purchase_${sessionId}`,
        });
      })
      .catch((err) => console.warn('[Handbook] session-status check failed', err));
  }, []);

  // Always pull the latest handbook URL from the backend so the page button
  // and the post-purchase email stay in lockstep with every PDF update.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/handbook/pdf-url`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data && data.url) setHandbookUrl(data.url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="hb-page">
      <section className="hb-thanks">
        {showConfetti && (
          <div className="hb-confetti" data-testid="thanks-confetti">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="hb-confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  backgroundColor: ['#b86c3a', '#c8a45c', '#1d8a4f', '#0B2239'][Math.floor(Math.random() * 4)]
                }}
              />
            ))}
          </div>
        )}

        <div className="hb-thanks-card" data-testid="thanks-card">
          <div className="hb-thanks-icon">
            <CheckCircle size={44} strokeWidth={1.6} />
          </div>

          <h1 className="hb-thanks-title">Thank you for your purchase</h1>
          <p className="hb-thanks-subtitle">
            Your Parents' Digital Safety Handbook is ready to download.
          </p>

          <div className="hb-thanks-summary">
            <div className="hb-thanks-row">
              <span>Product</span>
              <span>Parents' Digital Safety Handbook</span>
            </div>
            <div className="hb-thanks-row">
              <span>Amount paid</span>
              <span className="hb-thanks-amount">${amount} AUD</span>
            </div>
          </div>

          <a
            href={handbookUrl}
            className="hb-btn-primary hb-btn-large hb-thanks-download"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thanks-download-btn"
          >
            <Download size={18} />
            Download your handbook
          </a>

          <div className="hb-thanks-email">
            <Mail size={18} />
            <p>
              We've also emailed your download link. Check your inbox
              (and spam folder) for an email from <strong>All Secured</strong>.
            </p>
          </div>

          <div className="hb-thanks-tips">
            <h3>Where to start</h3>
            <ul>
              <li>
                <CheckCircle size={16} />
                <span>Begin with the <strong>Family Digital Agreement</strong> template at the back of the handbook.</span>
              </li>
              <li>
                <CheckCircle size={16} />
                <span>Read through the <strong>Warning Signs</strong> section so you know what to watch for.</span>
              </li>
              <li>
                <CheckCircle size={16} />
                <span>Bookmark the <strong>Australian resources</strong> directory for quick reference.</span>
              </li>
            </ul>
          </div>

          <div className="hb-thanks-footer">
            <Link to="/" className="hb-thanks-back" data-testid="thanks-back-home">
              <Shield size={16} />
              Back to All Secured
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParentsHandbookThankYou;
